/**
 * Modal d'ajout/édition d'heures supplémentaires
 * @module components/heures-sup/HeuresSupModal
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, CalendarIcon, Clock, Calculator } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

import type {
  HeureSup,
  HeuresSupSettings,
  TypeHeureSup,
  CreateHeureSupData,
} from '@/types/heures-sup.types';
import {
  genererApercuCalcul,
  getTypesHeuresSupDisponibles,
  getTranchesDisponibles,
  formatCoutHeuresSup,
  formatMajoration,
  formatTauxHoraire,
  OPTIONS_HEURES,
} from '@/lib/calculs-heures-sup';
import { useModalProtection } from '@/hooks/useModalProtection';

interface HeuresSupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chantierId: string;
  entrepriseId: string;
  membresAffectes: Array<{
    id: string;
    nom: string;
    prenom: string;
    taux_horaire: number;
  }>;
  editingEntry?: HeureSup;
  settings: HeuresSupSettings;
  onSave: (data: CreateHeureSupData) => Promise<void>;
}

export default function HeuresSupModal({
  open,
  onOpenChange,
  chantierId,
  entrepriseId,
  membresAffectes,
  editingEntry,
  settings,
  onSave,
}: HeuresSupModalProps) {
  useModalProtection(open);

  // États du formulaire
  const [membreId, setMembreId] = useState<string>('');
  const [dateTravail, setDateTravail] = useState<Date | undefined>(new Date());
  const [nombreHeures, setNombreHeures] = useState<number>(1);
  const [typeHeure, setTypeHeure] = useState<TypeHeureSup>('standard');
  const [trancheMajoration, setTrancheMajoration] = useState<number>(25);
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Charger les données si édition
  useEffect(() => {
    if (editingEntry) {
      setMembreId(editingEntry.membre_id);
      setDateTravail(parseISO(editingEntry.date_travail));
      setNombreHeures(editingEntry.nombre_heures);
      setTypeHeure(editingEntry.type_heure);
      setTrancheMajoration(editingEntry.taux_majoration);
      setNotes(editingEntry.notes || '');
    } else {
      // Réinitialiser pour une nouvelle entrée
      setMembreId('');
      setDateTravail(new Date());
      setNombreHeures(1);
      setTypeHeure('standard');
      setTrancheMajoration(settings.heures_sup_tranches[0]?.majoration || 25);
      setNotes('');
    }
  }, [editingEntry, settings, open]);

  // Trouver le membre sélectionné
  const membreSelectionne = useMemo(() => {
    return membresAffectes.find((m) => m.id === membreId);
  }, [membresAffectes, membreId]);

  // Types d'heures disponibles
  const typesDisponibles = useMemo(() => {
    return getTypesHeuresSupDisponibles(settings);
  }, [settings]);

  // Tranches disponibles pour le type standard
  const tranchesDisponibles = useMemo(() => {
    return getTranchesDisponibles(settings);
  }, [settings]);

  // Calculer l'aperçu
  const apercu = useMemo(() => {
    if (!membreSelectionne) return null;
    return genererApercuCalcul(
      nombreHeures,
      membreSelectionne.taux_horaire,
      typeHeure,
      settings,
      typeHeure === 'standard' ? trancheMajoration : undefined
    );
  }, [membreSelectionne, nombreHeures, typeHeure, settings, trancheMajoration]);

  // Validation
  const isValid = useMemo(() => {
    return membreId && dateTravail && nombreHeures > 0;
  }, [membreId, dateTravail, nombreHeures]);

  // Sauvegarder
  const handleSave = async () => {
    if (!isValid || !membreSelectionne || !dateTravail || !apercu) return;

    setSaving(true);
    try {
      const data: CreateHeureSupData = {
        entreprise_id: entrepriseId,
        chantier_id: chantierId,
        membre_id: membreId,
        date_travail: format(dateTravail, 'yyyy-MM-dd'),
        nombre_heures: nombreHeures,
        type_heure: typeHeure,
        taux_horaire_base: membreSelectionne.taux_horaire,
        taux_majoration: apercu.taux_majoration,
        cout_total: apercu.cout_total,
        notes: notes.trim() || undefined,
      };

      await onSave(data);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            {editingEntry ? 'Modifier les heures sup' : 'Ajouter des heures supplémentaires'}
          </DialogTitle>
          <DialogDescription>
            Saisissez les heures supplémentaires pour un membre de l'équipe
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Membre */}
          <div className="space-y-2">
            <Label htmlFor="membre">Membre *</Label>
            <Select value={membreId} onValueChange={setMembreId}>
              <SelectTrigger id="membre">
                <SelectValue placeholder="Sélectionner un membre" />
              </SelectTrigger>
              <SelectContent>
                {membresAffectes.map((membre) => (
                  <SelectItem key={membre.id} value={membre.id}>
                    {membre.prenom} {membre.nom} ({formatTauxHoraire(membre.taux_horaire)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !dateTravail && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTravail ? format(dateTravail, 'PPP', { locale: fr }) : 'Sélectionner une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTravail}
                  onSelect={setDateTravail}
                  locale={fr}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Vous pouvez sélectionner une date passée pour une saisie rétroactive
            </p>
          </div>

          {/* Nombre d'heures */}
          <div className="space-y-2">
            <Label htmlFor="heures">Nombre d'heures *</Label>
            <div className="flex gap-2">
              <Select
                value={nombreHeures.toString()}
                onValueChange={(v) => setNombreHeures(parseFloat(v))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPTIONS_HEURES.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground self-center">ou</span>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="24"
                value={nombreHeures}
                onChange={(e) => setNombreHeures(parseFloat(e.target.value) || 0)}
                className="w-24"
              />
            </div>
          </div>

          {/* Type d'heures sup */}
          <div className="space-y-2">
            <Label htmlFor="type">Type d'heures sup *</Label>
            <Select value={typeHeure} onValueChange={(v) => setTypeHeure(v as TypeHeureSup)}>
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typesDisponibles.map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                    disabled={type.disabled}
                  >
                    {type.label}
                    {type.disabled && ' (non activé)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tranche de majoration (pour type standard) */}
          {typeHeure === 'standard' && tranchesDisponibles.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="tranche">Tranche de majoration</Label>
              <Select
                value={trancheMajoration.toString()}
                onValueChange={(v) => setTrancheMajoration(parseInt(v))}
              >
                <SelectTrigger id="tranche">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tranchesDisponibles.map((tranche) => (
                    <SelectItem key={tranche.value} value={tranche.value.toString()}>
                      {tranche.label} — {tranche.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Finition urgente client"
              rows={2}
            />
          </div>

          {/* Aperçu du calcul */}
          {apercu && membreSelectionne && (
            <div className="p-4 border rounded-lg bg-muted/30 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calculator className="w-4 h-4" />
                Aperçu du calcul
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Taux horaire de base :</div>
                <div>{formatTauxHoraire(apercu.taux_horaire_base)}</div>
                <div className="text-muted-foreground">Majoration appliquée :</div>
                <div className="text-amber-500 font-medium">{formatMajoration(apercu.taux_majoration)}</div>
                <div className="text-muted-foreground">Taux majoré :</div>
                <div>{formatTauxHoraire(apercu.taux_majore)}</div>
              </div>
              <div className="pt-2 border-t flex justify-between items-center">
                <span className="font-medium">COÛT TOTAL :</span>
                <span className="text-lg font-bold text-primary">
                  {formatCoutHeuresSup(apercu.cout_total)}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={!isValid || saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editingEntry ? 'Modifier les heures sup' : 'Ajouter les heures sup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
